/**
 *   This is a service for a specific store module which uses the api.ts Axios instance as a base.
 *   We separate our files for easy managment and readibility.
 *   We export this as a service to be used in other areas of the application.
 */

import Api from '@/services/api';
import AppService from '@/services/app';
import Store from '@/store/index';

export default {

  resetTypes() {
    // reset store to empty
    Store.dispatch('vehicles/setVehicleTypes', {
      vehicleTypes: [],
    });
  },

  async listTypes(refreshFromAPI: boolean) {
    // check if we need to force an API refresh or have already fetched this data and it is in the Store
    if (refreshFromAPI || Store.getters['vehicles/vehicleTypes'].length < 1) {
      try {
        const response = await Api(true).get('/vehicle/list_types');
        
        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            this.resetTypes();
            return response.data.message;
          } else {
            Store.dispatch('vehicles/setVehicleTypes', {
              vehicleTypes: response.data,
            });
            return '';
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        this.resetTypes();
        if (error.response) {
          return error.response.statusText;
        } else {
          AppService.logSupportDebug('vehicles.ts - listTypes - 46 - ' + error);
          return Store.getters['app/messages'].couldNotConnect;
        }
      } 
    } else {
      // this data is ready to access in the Store
      return '';
    }
  },

  resetActive() {
    // reset store to empty
    Store.dispatch('vehicles/setActiveVehicles', {
      vehicles: [],
    });
  },

  async listActive(refreshFromAPI: boolean) {
    // check if we need to force an API refresh or have already fetched this data and it is in the Store
    if (refreshFromAPI || Store.getters['vehicles/activeVehicles'].length < 1) {
      try {
        const response = await Api(true).get('/vehicle/list_active');
        
        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            this.resetActive();
            return response.data.message;
          } else {
            Store.dispatch('vehicles/setActiveVehicles', {
              vehicles: response.data,
            });
            return '';
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        this.resetActive();
        if (error.response) {
          return error.response.statusText;
        } else {
          AppService.logSupportDebug('vehicles.ts - listActive - 89 - ' + error);
          return Store.getters['app/messages'].couldNotConnect;
        }
      } 
    } else {
      // this data is ready to access in the Store
      return '';
    }
  },

  resetDisposed() {
    // reset store to empty
    Store.dispatch('vehicles/setDisposedVehicles', {
      vehicles: [],
    });
  },

  async listDisposed(refreshFromAPI: boolean) {
    // check if we need to force an API refresh or have already fetched this data and it is in the Store
    if (refreshFromAPI || Store.getters['vehicles/disposedVehicles'].length < 1) {
      try {
        const response = await Api(true).get('/vehicle/list_disposed');
        
        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            this.resetDisposed();
            return response.data.message;
          } else {
            Store.dispatch('vehicles/setDisposedVehicles', {
              vehicles: response.data,
            });
            return '';
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        this.resetDisposed();
        if (error.response) {
          return error.response.statusText;
        } else {
          AppService.logSupportDebug('vehicles.ts - listDisposed - 132 - ' + error);
          return Store.getters['app/messages'].couldNotConnect;
        }
      } 
    } else {
      // this data is ready to access in the Store
      return '';
    }
  },

  create(params: {
    vehicleTypeId: number,
    code: string,
    make: string,
    model: string,
    yearModel: string,
    description?: string,
    vin: string,
    registrationNumber: string,
    odometer: number,
    cellNumber?: string,
    roadworthyDate: string,
    licenseEndDate: string,
    wheelAlignmentIntervalKm: number,
    nextWheelAlignmentOdo: number,
    serviceIntervalKm: number,
    nextServiceOdo: number,
    linkedTrailerId?: number,
  }) {
    return Api(true).post('/vehicle/create', params);
  },

  update(params: { 
    id: number,
    vehicleTypeId: number,
    code: string,
    make: string,
    model: string,
    yearModel: string,
    description?: string,
    vin: string,
    registrationNumber: string,
    odometer: number,
    cellNumber?: string,
    roadworthyDate: string,
    licenseEndDate: string,
    wheelAlignmentIntervalKm: number,
    nextWheelAlignmentOdo: number,
    serviceIntervalKm: number,
    nextServiceOdo: number,
    suspended: boolean,
  }) {
    return Api(true).post('/vehicle/update', params);
  },

  dispose(params: { 
    id: number,
    reason: string,
    date: string, 
  }) {
    return Api(true).post('/vehicle/dispose', params);
  },

  delete(params: { id: number }) {
    return Api(true).post('/vehicle/delete', params);
  },

  resetWorkshopSummary() {
    // reset store to empty
    Store.dispatch('vehicles/setWorkshopSummary', {
      workshopSummary: [],
    });
  },

  async listWorkshopSummary(refreshFromAPI: boolean) {
    // check if we need to force an API refresh or have already fetched this data and it is in the Store
    if (refreshFromAPI || Store.getters['vehicles/workshopSummary'].length < 1) {
      try {
        const response = await Api(true).get('/vehicle/workshop_summary');
        
        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            this.resetWorkshopSummary();
            return response.data.message;
          } else {
            Store.dispatch('vehicles/setWorkshopSummary', {
              workshopSummary: response.data,
            });
            return '';
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        this.resetWorkshopSummary();
        if (error.response) {
          return error.response.statusText;
        } else {
          AppService.logSupportDebug('vehicles.ts - listWorkshopSummary - 231 - ' + error);
          return Store.getters['app/messages'].couldNotConnect;
        }
      } 
    } else {
      // this data is ready to access in the Store
      return '';
    }
  },

 
};
